import { useMemo, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { gql, useLazyQuery, useQuery } from '@apollo/client'

import { deserializeErrors } from 'apollo-client'
import { useSites } from 'store'
import { useSort, useFilters } from './store'

interface IBypassHistory {
  actor: string,
  action: string,
  created_at: Date,

}
export interface IBypass {
  id: string,
  mac_address: string,
  active: boolean
  created_at: string | Date,
  deactivated_at: string | Date | null,
  description: string | null,
  history: IBypassHistory[]
}

const OPERATION_COUNT_BYPASSES = gql`
  query countBypasses($sfId: ID!) {
    results: countBypasses(site_sf_id: $sfId) {
      key
      count
    }
  }
`

const OPERATION_EXPORT = gql`
  query exportBypasses($sfId: ID!, $verbatim: String, $active: Boolean, $inactive: Boolean, $sort_by: String, $sort_direction: String) {
    base64: exportBypasses(site_sf_id: $sfId, verbatim: $verbatim, active: $active, inactive: $inactive, sort_by: $sort_by, sort_direction: $sort_direction)
  }
`
const OPERATION_BYPASSES = gql`
  query bypasses($sfId: ID!, $verbatim: String, $active: Boolean, $inactive: Boolean, $sort_by: String, $sort_direction: String, $page: Int, $limit: Int) {
    bypasses: bypasses(site_sf_id: $sfId, verbatim: $verbatim, active: $active, inactive: $inactive, sort_by: $sort_by, sort_direction: $sort_direction, page: $page, limit: $limit) {
      data {
        id
        mac_address
        description
        activated_at
        deactivated_at
        active
        created_at
        history {
          actor
          action
          timestamp
        }
      }
      pagination {
        current_page
        next_page
        has_next_page
        total
      }
    }
  }
`

const OPERATION_BYPASS = gql`
  query bypass($id: ID!) {
    bypass: bypass(id: $id) {
      id
      mac_address
      description
      activated_at
      deactivated_at
      active
      created_at
      history {
        actor
        action
        timestamp
      }
      created_by {
        email
      }
      updated_by {
        email
      }  
    }
  }
`
const PAGE_SIZE = 10

export const useExport = () => {
  const [filters] = useFilters()
  const [{ selected: sitesSelected }] = useSites()
  const [sort] = useSort()
  const [launchExport, { loading, error }] = useLazyQuery(OPERATION_EXPORT)
  const handleExport = async () => {
    const queryVariables = {
      sfId: sitesSelected[0]?.sf_id,
      verbatim: filters.verbatim || '',
      active: filters.state.includes('activated'),
      inactive: filters.state.includes('deactivated'),
      sort_by: sort.by,
      sort_direction: sort.direction
    }
    const { data: { base64 } } = await launchExport({ variables: queryVariables })
    const blob = await (await fetch(base64)).blob()
    return URL.createObjectURL(blob)
  }

  return {
    launch: handleExport,
    loading,
    // @ts-expect-error JSX
    error: error ? deserializeErrors(error)?.replaceAll('Errors::', '') : null,
  }
}

const useList = () => {
  const isLoadingMore = useRef<boolean>(false)
  const [filters] = useFilters()
  const [{ selected: sitesSelected }] = useSites()
  const [sort] = useSort()
  const sfId: string = useMemo(
    () => sitesSelected[0]?.sf_id as string,
    [sitesSelected]
  )

  const queryVariables = useMemo(() => ({
    sfId,
    verbatim: filters.verbatim || '',
    active: filters.state.includes('activated'),
    inactive: filters.state.includes('deactivated'),
    sort_by: sort.by,
    sort_direction: sort.direction,
    page: 1,
    limit: PAGE_SIZE
  }), [sfId, filters.verbatim, filters.state, sort.by, sort.direction])

  const { loading, data, error, fetchMore, networkStatus } = useQuery(OPERATION_BYPASSES, {
    notifyOnNetworkStatusChange: true,
    variables: queryVariables,
    skip: !sfId
  })

  const items = useMemo(() => {
    return data?.bypasses?.data || []
  }, [data])

  return {
    // @ts-expect-error JSX
    error: error ? deserializeErrors(error)?.replaceAll('Errors::', '') : null,
    loading: (loading && networkStatus !== 3) || networkStatus === 2,
    loadingNext: networkStatus === 3,
    shouldFetchNextPage: !loading && !isLoadingMore.current && data?.bypasses?.pagination?.has_next_page,
    fetchNextPage: async () => {
      if (networkStatus === 3 || isLoadingMore.current) {
        return
      }

      const nextPage = data?.bypasses?.pagination?.next_page
      if (!nextPage) {
        return
      }

      try {
        isLoadingMore.current = true

        await fetchMore({
          variables: {
            page: nextPage,
            limit: PAGE_SIZE
          },
          updateQuery: (prev, { fetchMoreResult }) => {
            if (!fetchMoreResult) return prev
            if (!prev.bypasses || !fetchMoreResult.bypasses) return prev

            // Avoid duplicates by checking IDs
            const existingIds = new Set(prev.bypasses.data.map(item => item.id))
            const newData = fetchMoreResult.bypasses.data.filter(item => !existingIds.has(item.id))

            const merged = {
              bypasses: {
                data: [...prev.bypasses.data, ...newData],
                pagination: fetchMoreResult.bypasses.pagination
              }
            }

            return merged
          }
        })
      } catch (e) {
        console.error('Error fetching next page:', e)
      } finally {
        isLoadingMore.current = false
      }
    },
    // Informations de pagination
    pagination: data?.bypasses?.pagination || {},
    items: items as IBypass[],
  }
}

export const useDetail = () => {
  const { id } = useParams()
  const { loading, error, data } = useQuery(OPERATION_BYPASS, {
    variables: {
      id
    },
    skip: !id
  })
  return {
    loading,
    // @ts-expect-error JSX
    error: error ? deserializeErrors(error)?.replaceAll('Errors::', '') : null,
    item: data?.bypass
  }
}

export const useCount = () => {
  const [{ selected: sitesSelected }] = useSites()
  const sfId: string = useMemo(
    () => sitesSelected[0]?.sf_id as string,
    [sitesSelected]
  )
  const { loading, data } = useQuery(OPERATION_COUNT_BYPASSES, {
    variables: {
      sfId
    },
    skip: !sfId
  })
  return {
    loading,
    active: data ? data.results.find(({ key }) => key === 'active')?.count || 0 : 0,
    inactive: data ? data.results.find(({ key }) => key === 'inactive')?.count || 0 : 0
  }
}
export default useList